import React, { useEffect, useState } from 'react';
import { Field, Form, Formik, FormikErrors } from 'formik';
import { v4 as generateUUID } from 'uuid';

import { MiniAppFocusWrapper } from '../miniapp-focus-wrapper/miniapp-focus-wrapper';
import { FormAddress, InputField, validateAddressField } from 'src/features/common';
import FullPageLoader from '../full-page-loader/full-page-loader';
import TerminalModal from '../terminal-modal/terminal-modal';
import useDebounce from 'src/hooks/use-debounce';
import {
  TerminalDetails,
  UpdateTerminalDetails,
  getTerminalDetails,
  updateTerminalDetails,
} from 'src/services/update-terminal';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import TealiumEvents from 'src/helpers/tealium-events';
import { formatMobileNumber, phoneNumberMaskSpaced, stripWhitespace } from 'src/helpers/formatting';
import { TerminalAddress } from 'src/containers/onboarding/create-account/terminal-input/terminal-address-input';
import { DateTime } from 'luxon';

type TerminalDetailsFormValues = {
  primaryContactNumber: string;
  secondaryContactNumber: string;
  address: FormAddress;
};

type TerminalDetailsFormError = {
  primaryContactNumber: string;
  secondaryContactNumber: string;
  address: FormAddress;
  autoAddress: string;
};

type TerminalType = {
  terminalType: string;
  terminalName: string;
  terminalID: string;
  status: string;
  terminalTypeId: number;
};

const TerminalDetailsForm = ({ terminal }: { terminal: TerminalType }) => {
  if (!terminal) {
    console.error('Terminal object is undefined');
    return;
  }
  const { terminalName, terminalID } = terminal;

  const [changeTerminalDetails, setChangeTerminalDetails] = useState(false);
  const [terminalDetails, setTerminalDetails] = useState<TerminalDetails>();
  const [showLoader, setShowLoader] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [terminalModal, setTerminalModal] = useState(false);
  const [terminalAddress, setTerminalAddress] = useState('');

  const selectedAddressDebounced = useDebounce(terminalAddress, 500);

  const { merchant } = useMerchantProfile();
  const fetchTerminalDetails = async (
    abortController: AbortController,
    merchantId: any,
    terminalID: string,
  ) => {
    try {
      const response = await getTerminalDetails(abortController, merchantId, terminalID);
      if (!response.ok) {
        toastMessageServiceSingleton.addError(response?.message, null, '', false, null, true);
        return;
      }
      setTerminalDetails(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchTerminalDetails(abortController, merchant?.merchantId, terminalID);

    return () => {
      abortController.abort();
    };
  }, [selectedAddressDebounced]);

  function validateTerminalForm(values: TerminalDetailsFormValues) {
    const errors: FormikErrors<TerminalDetailsFormError> = {};

    setTerminalAddress(typeof values.address === 'string' ? values.address : '');

    if (!values.primaryContactNumber) {
      errors.primaryContactNumber = 'Please enter a primary contact Number';
    }
    if (!values.secondaryContactNumber) {
      errors.secondaryContactNumber = 'Please enter a secondary contact Number';
    }
    if (!values.address) {
      errors.autoAddress = 'The address entered is invalid';
    } else {
      const addressError = validateAddressField(values.address);
      if (addressError) {
        // @ts-ignore
        errors.address = addressError;
      }
    }

    return errors;
  }

  async function submitTerminalDetailsForm(values: TerminalDetailsFormValues) {
    const abortController = new AbortController();
    const requestId = generateUUID();

    setShowLoader(true);

    const streetNumber: string = values.address.street !== undefined ? values.address.street : '';
    const streetName: string = values.address.building !== undefined ? values.address.building : '';
    const suburb: string = values.address.suburb !== undefined ? values.address.suburb : '';
    const city: string = values.address.city !== undefined ? values.address.city : '';
    const province: string = values.address.province !== undefined ? values.address.province : '';
    const postalCode: string =
      values.address.postalCode !== undefined ? values.address.postalCode : '';

    const data: UpdateTerminalDetails = {
      requestId: requestId,
      requestedDateTime: DateTime.now().toString(),
      primaryContactNumber: stripWhitespace(values.primaryContactNumber),
      secondaryContactNumber: stripWhitespace(values.secondaryContactNumber),
      streetNumber: streetNumber,
      streetName: streetName,
      suburb: suburb,
      city: city,
      province: province,
      postalCode: postalCode,
    };

    if (!merchant.merchantId) {
      setShowLoader(false);
      return;
    }
    TealiumEvents.userClicksSaveOnTerminal(terminal.terminalName);
    try {
      const result = await updateTerminalDetails(
        abortController,
        data,
        merchant?.merchantId,
        terminalID,
      );
      setShowLoader(false);
      setIsSuccess(true);
      setTerminalModal(true);
      setChangeTerminalDetails(false);

      if (!result.ok) {
        toastMessageServiceSingleton.addError(result.message, null, '', false, null, true);
        setShowLoader(false);
        return;
      }
    } catch (error) {
      setShowLoader(false);
      console.error(error);
    }
  }

  function onClick() {
    setChangeTerminalDetails(!changeTerminalDetails);
    TealiumEvents.terminalTab(terminal.terminalName);
  }

  const initialValues = (): TerminalDetailsFormValues => {
    return {
      primaryContactNumber: terminalDetails?.primaryContactNumber
        ? terminalDetails?.primaryContactNumber
        : '',
      secondaryContactNumber: terminalDetails?.secondaryContactNumber
        ? terminalDetails?.secondaryContactNumber
        : '',
      address: {
        street: terminalDetails?.streetNumber || '',
        building: terminalDetails?.streetName || '',
        suburb: terminalDetails?.suburb || '',
        city: terminalDetails?.city || '',
        province: terminalDetails?.province || '',
        postalCode: terminalDetails?.postalCode || '',
        country: '',
      },
    };
  };

  return (
    <>
      <Formik
        initialValues={initialValues()}
        validate={validateTerminalForm}
        onSubmit={submitTerminalDetailsForm}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={true}
        enableReinitialize
      >
        <Form className="flex flex-col gap-y-4 w-full max-w-[500px]">
          <section className="flex flex-row w-full gap-20">
            <p className="text-grey-400">{terminalName}</p>
            <p className="text-grey-400">{terminalID}</p>
          </section>
          {changeTerminalDetails ? (
            <>
              <section className="flex flex-col w-full gap-4 pl-5">
                <h3 className="text-lg text-white-700">Contact details</h3>
              </section>
              <Field
                name="primaryContactNumber"
                type="tel"
                component={MiniAppFocusWrapper}
                innerComponent={InputField}
                className="dark"
                fieldType="masked"
                mask={phoneNumberMaskSpaced}
                placeholder="Primary Contact Number"
                isDark
              />
              <Field
                name="secondaryContactNumber"
                type="tel"
                component={MiniAppFocusWrapper}
                innerComponent={InputField}
                className="dark"
                fieldType="masked"
                mask={phoneNumberMaskSpaced}
                placeholder="Secondary Contact Number"
                isDark
              />
              <section className="flex flex-col w-full gap-4 pl-5">
                <h3 className="text-lg text-white-700">Delivery / Trading address</h3>
              </section>

              <Field
                name="address"
                component={TerminalAddress}
                titleLowerCase="business address"
                isLightMode={true}
              />
            </>
          ) : (
            <>
              <section className="flex flex-col w-full gap-4 pl-5">
                <h3 className="text-lg text-white-700">Contact details</h3>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">Primary Contact Number</p>
                  <p className="text-sm text-grey-400">
                    {formatMobileNumber(terminalDetails?.primaryContactNumber ?? '')}
                  </p>
                </section>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">Secondary Contact Number</p>
                  <p className="text-sm text-grey-400">
                    {formatMobileNumber(terminalDetails?.secondaryContactNumber ?? '')}
                  </p>
                </section>
              </section>
              <section className="flex flex-col w-full gap-4 pl-5">
                <h3 className="text-lg text-white-700">Delivery / Trading address</h3>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">Street address</p>
                  <p className="text-sm text-grey-400">{terminalDetails?.streetNumber}</p>
                </section>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">Building or Complex (Optional)</p>
                  <p className="text-sm text-grey-400">{terminalDetails?.streetName}</p>
                </section>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">Suburb</p>
                  <p className="text-sm text-grey-400">{terminalDetails?.suburb}</p>
                </section>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">City / Town</p>
                  <p className="text-sm text-grey-400">{terminalDetails?.city}</p>
                </section>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">Province</p>
                  <p className="text-sm text-grey-400">{terminalDetails?.province}</p>
                </section>
                <section className="flex flex-col w-full">
                  <p className="text-xs text-white ">Postal Code</p>
                  <p className="text-sm text-grey-400">{terminalDetails?.postalCode}</p>
                </section>
              </section>
            </>
          )}
          <div className="flex w-full mt-6 gap-x-2">
            <button
              type={changeTerminalDetails ? 'reset' : 'button'}
              className="max-w-[6.5rem] w-full h-[46px] border-[2px] border-solid border-white p-2 rounded-40 disabled:bg-grey-700 disabled:border-none disabled:cursor-not-allowed"
              onClick={onClick}
            >
              {changeTerminalDetails ? 'Cancel' : 'Change'}
            </button>
            <button
              type="submit"
              className="max-w-[6.5rem] w-full h-[46px] border-[2px] border-solid border-white p-2 rounded-40 disabled:bg-grey-700 disabled:border-none disabled:cursor-not-allowed"
              disabled={!changeTerminalDetails}
            >
              Save
            </button>
          </div>
        </Form>
      </Formik>
      <FullPageLoader showLoader={showLoader} />
      <TerminalModal show={terminalModal} setShow={setTerminalModal} isSuccess={isSuccess} />
    </>
  );
};

export default TerminalDetailsForm;
